import { useEffect, useRef, useState, useCallback } from "react"
import { cn } from "@/lib/utils"
import { useSidebar } from "@/components/ui/sidebar"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { PRODUCT_TOUR_STEPS } from "@/modules/product-tour/constants/steps"
import ActivitiesWeeklyActions from "../activities/weekly/activities-weekly-actions"
import WeeklyActivitiesFilter from "../activities/weekly/weekly-activities-filter"
import DailyCalendarTopbarActions from "../daily-calendar/topbar-actions"
import { useActivitiesStore } from "../../stores/use-activities-store"

interface StudyHeaderProps {
  selectedTab: string
  handleOnChangeTab: (value: string) => void
  scrollWeeklyRef: React.RefObject<HTMLDivElement>
  onStickyChange?: (isSticky: boolean) => void
}

export default function StudyHeader({
  handleOnChangeTab,
  scrollWeeklyRef,
  selectedTab,
  onStickyChange,
}: StudyHeaderProps) {
  const { activities } = useActivitiesStore()
  const [isSticky, setIsSticky] = useState(false)
  const headerRef = useRef<HTMLElement>(null)
  const initialPositionRef = useRef<number | null>(null)
  const lastScrollY = useRef(window.scrollY)
  const { state } = useSidebar()
  const frameRef = useRef<number | null>(null)

  // Set the initial position once and keep it stable
  useEffect(() => {
    if (headerRef.current && initialPositionRef.current === null) {
      const headerPosition = headerRef.current.getBoundingClientRect()
      initialPositionRef.current = headerPosition.top + window.scrollY
    }
    // Verify if it should be sticky based on the current scroll position
    if (initialPositionRef.current !== null && window.scrollY > initialPositionRef.current) {
      const newStickyState = true
      setIsSticky(newStickyState)
      onStickyChange?.(newStickyState)
    }
  }, [onStickyChange])

  const handleScroll = useCallback(() => {
    if (frameRef.current !== null) {
      return
    }

    // Use requestAnimationFrame to limit updates
    frameRef.current = requestAnimationFrame(() => {
      if (initialPositionRef.current !== null) {
        const currentScrollY = window.scrollY
        // Only update if there is a significant change in scroll position
        if (Math.abs(currentScrollY - lastScrollY.current) > 5) {
          const shouldBeSticky = currentScrollY > initialPositionRef.current
          if (shouldBeSticky !== isSticky) {
            setIsSticky(shouldBeSticky)
            onStickyChange?.(shouldBeSticky)
          }
          lastScrollY.current = currentScrollY
        }
      }
      frameRef.current = null
    })
  }, [isSticky, onStickyChange])

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => {
      window.removeEventListener("scroll", handleScroll)
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current)
        frameRef.current = null
      }
    }
  }, [handleScroll])

  return (
    <header
      ref={headerRef}
      data-sidebar-state={state}
      className={cn(
        "mt-6 flex flex-col gap-4",
        isSticky && "fixed top-0 z-50 mt-0 border-0 bg-white px-6 py-4",
        isSticky && state === "expanded" && "left-64 w-[calc(100%-16rem)]",
        isSticky && state === "collapsed" && "left-[72px] w-[calc(100%-72px)]",
        !isSticky && "w-full",
      )}
    >
      <h2 className="p-2 pb-0 text-xl font-bold text-muted-foreground">Mis actividades</h2>
      <div className="flex h-fit flex-row justify-between pl-1">
        <span className="flex h-full w-full self-center">
          <Tabs
            value={selectedTab}
            onValueChange={handleOnChangeTab}
            defaultValue="weekly"
            id={PRODUCT_TOUR_STEPS.STUDY_CHANGE_VIEW}
          >
            <TabsList className="overflow-hidden rounded-lg">
              <TabsTrigger
                value="daily"
                className="text-md border-1 data-[state=active]:border-1 rounded-bl-lg rounded-tl-lg border border-transparent bg-neutral-200/50 px-12 data-[state=active]:border data-[state=active]:border-border data-[state=active]:bg-white data-[state=active]:text-neutral-700"
              >
                Hoy
              </TabsTrigger>
              <TabsTrigger
                value="weekly"
                className="text-md border-1 data-[state=active]:border-1 rounded-br-lg rounded-tr-lg border border-transparent bg-neutral-200/50 px-12 data-[state=active]:border data-[state=active]:border-border data-[state=active]:bg-white data-[state=active]:text-neutral-700"
              >
                Semana
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </span>
        <div className="hidden h-fit gap-2 self-center md:flex">
          {selectedTab === "weekly" ? (
            <>
              <ActivitiesWeeklyActions scrollRef={scrollWeeklyRef} />
              {activities.length > 0 && <WeeklyActivitiesFilter activities={activities} />}
            </>
          ) : (
            <DailyCalendarTopbarActions />
          )}
        </div>
      </div>
    </header>
  )
}
